import React, { useMemo, useRef, useState } from 'react';
import moment, { Moment } from "moment";
import { FloatingUserCell } from "./FloatingUserCell";
import {
  BorderDayCell,
  DAY_WIDTH,
  ROW_HEIGHT,
  USER_ROW_HEIGHT,
  USER_ROW_HEIGHT_MOBILE,
  WallPlannerRow,
  WEEK_WIDTH,
  WeekCell
} from '../WallPlannerStructuralComponents';
import styled, { css } from "styled-components";
import { UserAvatarAndName } from '@shared/ui/molecules/UserAvatarAndName';
import { BasicUserInfo, Birthday, Permission } from '@shared/models/user.models';
import { useDispatch, useSelector } from "react-redux";
import { BodySmall, BodyVerySmall } from '@shared/ui/atoms/fonts/Body';
import { selectCurrentUser, selectIconPack } from '@shared/store/auth.duck';
import { smallTablet } from '@shared/ui/atoms/MediaQueries';
import { HolidayBlock } from "./HolidayBlock";
import { WithWeeksInCurrentCalendarMonth } from '@features/whereabouts/hooks/WithWeekInCurrentCalendarMonth';
import { MemberMovements, Period, WhereaboutsGroupType } from '@features/whereabouts/models/movements.models';
import {
  selectActiveDay,
  selectEditingWhereabouts,
  selectEditScope,
  selectFocusedGroupId,
  selectFocusedUserId,
  startEditingWhereabouts
} from '@features/whereabouts/store/editMovements.duck';
import { canUserEditUser } from '@shared/utils/AccessControl';
import { SimpleSpinner } from '@shared/ui/atoms/SimpleSpinner';
import { CompanyHoliday } from '@features/holidays/services/CompanyHolidayService';
import { CompanyHolidayBlock } from "./CompanyHolidayBlock";
import { CheckLineReport } from "@shared/hooks/management-structure/CheckLineReport";
import Icon, { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { FloatingBirthdayIcon } from "./FloatingBirthdayIcon";
import { CustomLeaveDays, NonWorkingDays, SchengenUsage } from '@features/holidays/services/HolidayService';
import { NonWorkingDayBlock } from "./NonWorkingDayBlock";
import { CustomLeaveBlock } from "./CustomLeaveBlock";
import { ActivityBlockV2 } from "./WallPlannerActivityBlocksV2";
import { SchengenIcon, SCHENGEN_DAY_ALLOWANCE, schengenPalette } from "./SchengenIcon";
import { selectSchengenCountsVisible } from '@features/whereabouts/store/whereaboutsView.duck';
import { useTranslation } from "react-i18next";
import { DATE_FORMAT, PRETTY_DATE_NO_ORDINAL_FORMAT } from '@shared/utils/DateUtils';
import { HoverPopup } from '@shared/ui/molecules/HoverPopup';
import Colours from '@shared/ui/atoms/Colours';

export interface WallPlannerActivity {
  startDate: Moment;
  endDate: Moment;
}

interface Props {
  user?: BasicUserInfo;
  holidays?: WallPlannerActivity[];
  working?: WallPlannerActivity[];
  companyHolidays?: CompanyHoliday[];
  nonWorkingDays?: NonWorkingDays[];
  customNonWorkingDays?: NonWorkingDays[];
  customLeaveDays?: CustomLeaveDays[];
  schengenUsage?: SchengenUsage[];
  children?: any;
  id?: string;
  position?: number;
  isLeaveManagementView?: boolean;
}

export function WallPlannerUserRow(props: Props) {
  const {user, holidays, working, children,
    companyHolidays, nonWorkingDays, customLeaveDays,
  schengenUsage, customNonWorkingDays} = props;
  const currentUser = useSelector(selectCurrentUser);
  const iconPack = useSelector(selectIconPack);
  const showSchengenCounts = useSelector(selectSchengenCountsVisible);
  // Rows only need the extra height for the pill, and they all grow together so the grid stays even
  // for people who have no schengen days of their own.
  const showingSchengen = !!currentUser?.companyEntity?.schengenTracking && showSchengenCounts;
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const weeksInMonth = WithWeeksInCurrentCalendarMonth();
  const isLineReport = CheckLineReport(currentUser, user?.userId);

  const canSetWeekWhereabouts = useMemo(() => {
    if (!user || !currentUser) return false;
    if (currentUser.id === user.userId) return true;
    return canUserEditUser(currentUser, {id: user.userId, teamId: user.teamId, departmentId: user.departmentId} as MemberMovements);
  }, [currentUser, user]);

  const onSetDayClick = (date: Moment) => {
    if (!user) return;
    dispatch(startEditingWhereabouts({
      userId: user.userId,
      date: date.clone(),
      groupId: WhereaboutsGroupType.Users,
      scope: 'week',
    }));
  };

  const sevenDayWeek = !!currentUser?.companyEntity?.sevenDayWhereaboutsWeekEnabled;

  const focusedUserId = useSelector(selectFocusedUserId);
  const focusedGroupId = useSelector(selectFocusedGroupId);
  const editScope = useSelector(selectEditScope);
  const activeEditDay = useSelector(selectActiveDay);
  const editingWhereabouts = useSelector(selectEditingWhereabouts);
  const isEditingThisRow = !!user && focusedUserId === user.userId
    && focusedGroupId === WhereaboutsGroupType.Users && editScope === 'week';

  // The activity blocks are absolutely-positioned siblings painted over the
  // week cells, so a cell's own :hover/click never fires while the pointer is
  // over a block. Track the pointer at row level instead (events bubble up
  // from the blocks) and work out which week and day it is in from the cell
  // rects.
  const weekCellRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [hoveredWeekIdx, setHoveredWeekIdx] = useState<number | null>(null);
  const [hoveredDayIdx, setHoveredDayIdx] = useState<number | null>(null);

  const dayUnderPointer = (e: React.MouseEvent): {weekIdx: number, dayIdx: number} | null => {
    const weekIdx = weekCellRefs.current.findIndex(el => {
      if (!el) return false;
      const rect = el.getBoundingClientRect();
      return e.clientX >= rect.left && e.clientX < rect.right && e.clientY >= rect.top && e.clientY < rect.bottom;
    });
    if (weekIdx < 0) return null;
    const rect = weekCellRefs.current[weekIdx]!.getBoundingClientRect();
    const dayIdx = Math.min(6, Math.max(0, Math.floor((e.clientX - rect.left) / (rect.width / 7))));
    if (!sevenDayWeek && dayIdx > 4) return {weekIdx, dayIdx: -1};
    return {weekIdx, dayIdx};
  };

  const onRowMouseMove = (e: React.MouseEvent) => {
    if (!canSetWeekWhereabouts) return;
    const hit = dayUnderPointer(e);
    setHoveredWeekIdx(hit ? hit.weekIdx : null);
    setHoveredDayIdx(hit && hit.dayIdx >= 0 ? hit.dayIdx : null);
  };

  const onRowClick = (e: React.MouseEvent) => {
    if (!canSetWeekWhereabouts) return;
    const hit = dayUnderPointer(e);
    if (!hit || hit.dayIdx < 0) return;
    onSetDayClick(weeksInMonth[hit.weekIdx].clone().add(hit.dayIdx, 'days'));
  };

  const canUserViewPopup = useMemo(() => {
    if (!window.location.href.includes('/holidays')) {
      return false;
    }

    if (!currentUser?.companyEntity.enableHolidayBooking) {
      return false;
    }

    if (currentUser?.role === Permission.CompanyAdmin) {
      return true;
    }

    if (currentUser?.role === Permission.TeamAdmin && currentUser.teamEntity !== null && currentUser.teamEntity.id === user?.teamId) {
      return true;
    }

    return isLineReport;
  }, [currentUser?.companyEntity.enableHolidayBooking, currentUser?.role, currentUser?.teamEntity, user?.teamId, isLineReport]);

  const birthday: Birthday | undefined = (currentUser?.companyEntity.showBirthdays && user?.birthdayDay && user.birthdayMonth) ? {day: user.birthdayDay, month: user.birthdayMonth} : undefined
  const firstDay = weeksInMonth[0];

  // Days still available once the period on screen has played out, taken from the usage on the last
  // day of the calendar rather than today.
  const schengen = useMemo(() => {
    if (!schengenUsage || schengenUsage.length === 0 || !firstDay) return undefined;
    const lastDateOnCalendar = firstDay.clone().add(weeksInMonth.length, 'weeks').subtract(1, 'days').format(DATE_FORMAT);
    const usageInPeriod = schengenUsage.filter(usage => usage.date <= lastDateOnCalendar);
    if (usageInPeriod.length === 0) return undefined;
    const endOfPeriod = usageInPeriod.reduce((latest, usage) => usage.date > latest.date ? usage : latest);
    return {
      daysUsed: endOfPeriod.daysUsed,
      daysLeft: Math.max(0, SCHENGEN_DAY_ALLOWANCE - endOfPeriod.daysUsed),
      endOfPeriod: moment(endOfPeriod.date).format(PRETTY_DATE_NO_ORDINAL_FORMAT),
      palette: schengenPalette(endOfPeriod.daysUsed),
    };
  }, [schengenUsage, firstDay, weeksInMonth.length]);

  if (!weeksInMonth || weeksInMonth.length === 0) {
    return null;
  }

  // Same horizontal footprint as ActivityFill so the overlays sit exactly on
  // the whereabouts fills.
  const dayBandStyle = (dayNumber: number) => ({
    left: dayNumber * DAY_WIDTH + WEEK_WIDTH + 2,
    width: DAY_WIDTH - 6,
  });
  const dayNumberOf = (date: Moment) => date.clone().startOf('day').diff(firstDay.clone().startOf('day'), 'days');
  const daysOnCalendar = weeksInMonth.length * 7;
  const targetDayNumber = isEditingThisRow && activeEditDay ? dayNumberOf(activeEditDay) : null;
  const hoverDayNumber = hoveredWeekIdx !== null && hoveredDayIdx !== null ? hoveredWeekIdx * 7 + hoveredDayIdx : null;

  return (
    <WallPlannerUserRowContainer weekCount={weeksInMonth.length} id={props?.id ?? ''} position={props.position}
                                 $showingSchengen={showingSchengen}
                                 onMouseMove={onRowMouseMove}
                                 onClick={onRowClick}
                                 onMouseLeave={() => { setHoveredWeekIdx(null); setHoveredDayIdx(null); }}>
      <FloatingUserCell>
        {children && <>
            {children}
        </>}
        {user && !children && <UserCellContent>
          <UserAvatarAndName colour={currentUser?.id === user.userId ? 'blue' : 'darkBlue'}
                             onHoverIcon={canUserViewPopup ? IconTypes.Holiday : undefined}
                             showHolidayContentOnHover={!!props.isLeaveManagementView}
                             showUserHoverInfo={true}
                             basicUserInfo={user}
                             userId={user.userId}
                             firstName={user.firstName}
                             lastName={user.lastName}/>
          {showSchengenCounts && schengen &&
            <HoverPopup align={'left'}
                        style={{alignSelf: 'flex-start', margin: '2px 0 0 44px'}}
                        trigger={
                          <SchengenDaysLeftPill palette={schengen.palette}>
                            <SchengenPillIcon src={schengen.palette.icon} alt="" />
                            {t('work-planner.schengen-days-left', '{{days}} days left', {days: schengen.daysLeft})}
                          </SchengenDaysLeftPill>
                        }
                        popup={
                          <>
                            <BodySmall weight={600}>{t('work-planner.schengen-days-left-title', 'Schengen days left')}</BodySmall>
                            <BodyVerySmall colour={Colours.darkGrey}>
                              {t('work-planner.schengen-days-left-explanation',
                                '{{used}} of {{allowance}} days used by {{date}}, counted over the 180 days before that date.',
                                {used: schengen.daysUsed, allowance: SCHENGEN_DAY_ALLOWANCE, date: schengen.endOfPeriod})}
                            </BodyVerySmall>
                          </>
                        } />}
        </UserCellContent>}

      </FloatingUserCell>
      {weeksInMonth.map((timeFrame, k) => (
        <EditableWeekCell key={k} $clickable={canSetWeekWhereabouts}
                          ref={(el: HTMLDivElement | null) => { weekCellRefs.current[k] = el; }}>
          <BorderDayCell/>
          <BorderDayCell/>
          <BorderDayCell/>
          <BorderDayCell/>
          <BorderDayCell/>
          <BorderDayCell weekend={true}/>
          <BorderDayCell weekend={true}/>
        </EditableWeekCell>
      ))}

      <UserActivitiesContainer $clickable={canSetWeekWhereabouts}>
        {targetDayNumber !== null && targetDayNumber >= 0 && targetDayNumber < daysOnCalendar &&
          <TargetDayOutline style={dayBandStyle(targetDayNumber)}/>}
        {canSetWeekWhereabouts && hoverDayNumber !== null && hoverDayNumber !== targetDayNumber &&
          <DayTargetOutline style={dayBandStyle(hoverDayNumber)}/>}
        {isEditingThisRow && editingWhereabouts.map(day => {
          const dayNumber = dayNumberOf(moment(day.date));
          if (dayNumber < 0 || dayNumber >= daysOnCalendar) return null;
          const saving = !!day.isWhereaboutsSaving;
          const failed = !!day.whereaboutsUpdateFailed;
          if (!saving && !failed) return null;
          return (
            <DayStatusOverlay key={day.date} style={dayBandStyle(dayNumber)}>
              {saving ? <SimpleSpinner size={16}/> : <Icon icon={IconTypes.Alert} color="red"/>}
            </DayStatusOverlay>
          );
        })}
        {working?.map((activity: any, key) => (
          <React.Fragment key={key}>
            {(activity?.amOption?.key !== activity?.pmOption?.key) ? <HalfDayColumn>
                <ActivityBlockV2 iconPack={iconPack} style={{top: -16, height: 14}} whereabouts={activity} sideOfDay={Period.AM} firstDay={firstDay} birthday={birthday} />
                <ActivityBlockV2 iconPack={iconPack} style={{top: 2, height: 14}} whereabouts={activity} sideOfDay={Period.PM} firstDay={firstDay} birthday={birthday} />
              </HalfDayColumn> :
              <ActivityBlockV2 iconPack={iconPack}
                               whereabouts={activity}
                               firstDay={firstDay}
                               sideOfDay={Period.AllDay}
                               birthday={birthday} />
            }
          </React.Fragment>
        ))}

        {holidays?.map((activity: any, key) => (
          <HolidayBlock activity={activity} key={key}
                        firstDateOnCalendar={firstDay}
                        weeksInMonth={weeksInMonth.length}
                        user={props.user}
                        birthday={birthday}>
            <BodyVerySmall weight={600}>{activity?.name ?? ''}</BodyVerySmall>
          </HolidayBlock>
        ))}

        {nonWorkingDays?.map((activity: any, key) => (
          <NonWorkingDayBlock activity={activity} key={key}
                              firstDateOnCalendar={firstDay}
                              weeksInMonth={weeksInMonth.length}
                              birthday={birthday} />
        ))}

        {customNonWorkingDays?.map((activity: any, key) => (
          <NonWorkingDayBlock activity={activity} key={key}
                              firstDateOnCalendar={firstDay}
                              weeksInMonth={weeksInMonth.length}
                              birthday={birthday} />
        ))}

        {customLeaveDays?.map((activity: any, key) => (
          <CustomLeaveBlock activity={activity} key={key}
                            firstDateOnCalendar={firstDay}
                            weeksInMonth={weeksInMonth.length}
                            birthday={birthday} />
        ))}

        {companyHolidays?.map((activity: any, key) => (
          <CompanyHolidayBlock activity={activity} key={key}
                               firstDateOnCalendar={firstDay}
                               weeksInMonth={weeksInMonth.length}
                               birthday={birthday}>
            <BodyVerySmall weight={600}>{activity?.name ?? ''}</BodyVerySmall>
          </CompanyHolidayBlock>
        ))}

        {showSchengenCounts && schengenUsage?.map((activity: any, key: number) => (
          <SchengenIcon key={key}
                        used={activity.daysUsed}
                        date={activity.date}
                        weeksInMonth={weeksInMonth.length}
                        firstDateOnCalendar={firstDay}
                        allUsages={schengenUsage} />
        ))}
      </UserActivitiesContainer>

      {birthday && <FloatingBirthdayIcon birthday={birthday} monthStartDate={firstDay} weeksInMonth={weeksInMonth.length} />}

    </WallPlannerUserRowContainer>
  )
}


const WallPlannerUserRowContainer = styled(WallPlannerRow)<any>`
  height: ${USER_ROW_HEIGHT_MOBILE}px;
  width: 100%;
  transition: filter 0.15s;
  &:hover {
    filter: brightness(0.97);
  }
  @media (${smallTablet}) {
    height: ${props => props.$showingSchengen ? USER_ROW_HEIGHT : ROW_HEIGHT}px;
    left: 0;
  }
`

const EditableWeekCell = styled(WeekCell)<{ $clickable?: boolean }>`
  position: relative;
  ${props => props.$clickable ? 'cursor: pointer;' : ''}
`

// Rendered inside UserActivitiesContainer alongside the whereabouts fills,
// with the fills' left/width formula and their 32px band, which is centered
// on the container's top edge (see the split-day fills at top -16/+2), so the
// overlays sit exactly on the whereabouts band.
const dayOverlayBand = css`
  position: absolute;
  top: -16px;
  height: 32px;
  box-sizing: border-box;
  border-radius: 4px;
  pointer-events: none;
  z-index: 4;
`

const DayTargetOutline = styled.div`
  ${dayOverlayBand};
  border: 2px dashed #515BB0;
`

const TargetDayOutline = styled.div`
  ${dayOverlayBand};
  border: 2px solid #515BB0;
`

const DayStatusOverlay = styled.div`
  ${dayOverlayBand};
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.6);
`

const UserCellContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
`

const SchengenPillIcon = styled.img`
  width: 16px;
  height: 16px;
`

const SchengenDaysLeftPill = styled.div<any>`
  display: flex;
  padding: 2px 8px;
  align-items: center;
  gap: 5px;
  border-radius: 45px;
  background: ${props => props.palette.background};
  color: ${props => props.palette.text};
  font-family: "Proxima Nova", proxima-nova, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 18px;
  white-space: nowrap;
`

const UserActivitiesContainer = styled.div<{ $clickable?: boolean }>`
  position: absolute;
  left: -${WEEK_WIDTH}px;
  top: 40px;
  ${props => props.$clickable ? 'cursor: pointer;' : ''}
  @media (${smallTablet}) {
    left: 0;
    top: 28px;
  }
`

const HalfDayColumn = styled.div`
  display: flex;
  flex-direction: column;
`
